import React, { useEffect, useRef } from 'react';
import { View, Animated, StyleSheet, ViewStyle } from 'react-native';
import { createShimmerAnimation } from '../utils/animations';

interface SkeletonLoaderProps {
  width?: number | string;
  height?: number;
  borderRadius?: number;
  style?: ViewStyle;
}

export const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({
  width = '100%',
  height = 20,
  borderRadius = 4,
  style,
}) => {
  const shimmerAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const shimmer = createShimmerAnimation(shimmerAnim);
    shimmer();
  }, []);

  const shimmerStyle = {
    opacity: shimmerAnim.interpolate({
      inputRange: [0, 0.5, 1],
      outputRange: [0.3, 0.7, 0.3],
    }),
  };

  return (
    <View
      style={[
        styles.container,
        {
          width,
          height,
          borderRadius,
        },
        style,
      ]}
    >
      <Animated.View style={[styles.shimmer, shimmerStyle]} />
    </View>
  );
};

export const SkeletonCard: React.FC = () => {
  return (
    <View style={styles.card}>
      <View style={styles.cardHeader}>
        <SkeletonLoader width={120} height={16} />
        <SkeletonLoader width={60} height={14} />
      </View>
      <View style={styles.cardContent}>
        <SkeletonLoader width="100%" height={12} style={{ marginBottom: 8 }} />
        <SkeletonLoader width="80%" height={12} style={{ marginBottom: 8 }} />
        <SkeletonLoader width="60%" height={12} />
      </View>
    </View>
  );
};

export const SkeletonChart: React.FC = () => {
  return (
    <View style={styles.chartContainer}>
      <View style={styles.chartArea}>
        <SkeletonLoader width={120} height={120} borderRadius={60} />
        <View style={styles.legendArea}>
          {[1, 2, 3, 4].map((item) => (
            <View key={item} style={styles.legendItem}>
              <SkeletonLoader width={12} height={12} borderRadius={6} />
              <SkeletonLoader width={80} height={12} style={{ marginLeft: 8 }} />
            </View>
          ))}
        </View>
      </View>
    </View>
  );
};

export const SkeletonProductList: React.FC = () => {
  return (
    <View style={styles.productList}>
      {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
        <View key={item} style={styles.productCard}>
          {/* ISIN Column */}
          <View style={styles.productColumn1}>
            <SkeletonLoader width={80} height={14} />
          </View>
          {/* Name Column */}
          <View style={styles.productColumn2}>
            <SkeletonLoader width="90%" height={14} style={{ marginBottom: 4 }} />
            <SkeletonLoader width="70%" height={12} />
          </View>
          {/* Bid Column */}
          <View style={styles.productColumn3}>
            <SkeletonLoader width={50} height={14} />
          </View>
          {/* Ask Column */}
          <View style={styles.productColumn4}>
            <SkeletonLoader width={50} height={14} />
          </View>
          {/* CCY Column */}
          <View style={styles.productColumn5}>
            <SkeletonLoader width={35} height={14} />
          </View>
        </View>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#f1f5f9',
    overflow: 'hidden',
  },
  shimmer: {
    flex: 1,
    backgroundColor: '#e2e8f0',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 2,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  cardContent: {
    gap: 4,
  },
  chartContainer: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
  },
  chartArea: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 20,
  },
  legendArea: {
    flex: 1,
    gap: 8,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  productList: {
    gap: 8,
  },
  productCard: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 12,
    backgroundColor: '#fff',
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  productColumn1: {
    width: 80,
    marginRight: 8,
  },
  productColumn2: {
    flex: 1,
    marginRight: 8,
  },
  productColumn3: {
    width: 60,
    alignItems: 'flex-end',
    marginRight: 8,
  },
  productColumn4: {
    width: 60,
    alignItems: 'flex-end',
    marginRight: 8,
  },
  productColumn5: {
    width: 50,
    alignItems: 'center',
  },
});